import * as z from 'zod';

/**
 * PAY-4612: the release note history behind the update notice's arrows and
 * the profile menu's "What's new".
 *
 * `list_app_release_notes` returns published, well-formed notes, newest
 * first. The notice pages through them oldest to newest, left to right, so
 * "previous" is the older release.
 */

const MAX_POEM_LENGTH = 480;

const HistoryItemSchema = z.object({
  release_id: z.string().min(1),
  release_number: z.string().nullish(),
  poem: z.string(),
  published_at: z.string().nullish(),
});

export type ReleaseNoteEntry = {
  releaseId: string;
  releaseNumber: string | null;
  /** The verse, line breaks included. Null shows the title alone. */
  poem: string | null;
  publishedAt: string | null;
};

/** Newest first. Anything unexpected is dropped, never thrown. */
export function interpretReleaseNoteHistory(
  value: unknown,
): ReleaseNoteEntry[] {
  if (!Array.isArray(value)) return [];
  const entries: ReleaseNoteEntry[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    const parsed = HistoryItemSchema.safeParse(item);
    if (!parsed.success || seen.has(parsed.data.release_id)) continue;
    const poem = parsed.data.poem.trim();
    if (!poem || poem.length > MAX_POEM_LENGTH) continue;
    seen.add(parsed.data.release_id);
    entries.push({
      releaseId: parsed.data.release_id,
      releaseNumber: parsed.data.release_number?.trim() || null,
      poem,
      publishedAt: parsed.data.published_at ?? null,
    });
  }
  return entries;
}

/**
 * The pages the notice steps through, oldest first, and where it opens.
 *
 * With `current` (the release that was just claimed) the notice opens on it.
 * Its own claim answer wins over the history's copy, and a release with no
 * published note (the generic notice) still gets a page, at the newest end.
 * Without it, the notice opens on the newest note.
 */
export function buildReleaseNoteSequence(
  history: ReleaseNoteEntry[],
  current?: ReleaseNoteEntry,
): { entries: ReleaseNoteEntry[]; startIndex: number } {
  const oldestFirst = [...history].reverse();
  if (!current) {
    return { entries: oldestFirst, startIndex: oldestFirst.length - 1 };
  }

  const at = oldestFirst.findIndex(
    (entry) => entry.releaseId === current.releaseId,
  );
  if (at === -1) {
    const entries = [...oldestFirst, current];
    return { entries, startIndex: entries.length - 1 };
  }

  const entries = [...oldestFirst];
  entries[at] = {
    ...current,
    poem: current.poem ?? entries[at]!.poem,
    publishedAt: entries[at]!.publishedAt,
  };
  return { entries, startIndex: at };
}
